import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {StyledTableContainer, StyledTransactionTable, TotalSpend} from './StyledTransactionTable';
import {amountHelpers, dateHelpers, transactionsHelpers} from '../../../../../../utils/helpers';

const groupedPropName = 'isYearlyGrouping';

const isGroupedRow = (row) => row[groupedPropName];

const gMV = amountHelpers.getAmountWithCurrencyCode;

const TransactionTable = ({
  dispatch,
  categories,
  data = [],
  edit = true,
  loading = false,
  onExpenseUpdate,
  onRowClick,
  totalSpend = 0,
  selectedRowIndex,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'transactions']);

  const columns = [
    {
      dataIndex: 'created_date',
      title: t('date'),
      render: (date, record) => {
        const isGrouped = isGroupedRow(record);
        return dateHelpers.getDateWithMonth(date, isGrouped ? 'YYYY' : 'DD MMM');
      }
    },
    {
      dataIndex: 'type',
      title: t('type'),
      render: (_, record) => !isGroupedRow(record) && transactionsHelpers.getTransactionType(record)
    },
    {
      dataIndex: 'source',
      title: t('transactions:source'),
      render: (_, transaction) => transactionsHelpers.getTransactionSourceWithIcon({categories, transaction}),
    },
    {
      dataIndex: 'amount',
      title: t('amount'),
      render: (_, record) => {
        let {amount} = record;
        if (isGroupedRow(record)) {
          return <span className='total-amount'>{gMV(amount)}</span>;
        } else {
          let className = '';
          const isReturnedTransaction = transactionsHelpers.isReturnedTransaction(record);
          const isIncoming = record.is_incoming || false;
          if (!isIncoming && !isReturnedTransaction) className = `${className} danger-text`;
          if (isReturnedTransaction) className = `${className} returned-price`;
          return amount ? isIncoming ? gMV(amount) : <span className={className}>-{gMV(Math.abs(amount))}</span> : null;
        }
      }
    },
    {
      dataIndex: 'attachment',
      align: 'right',
      render: (_, transaction) => transactionsHelpers.getTransactionAttachmentIcon({transaction, translation: t}),
      width: 32
    }
  ];

  const handleOnRowClick = (e, record, index) => {
    const {id} = record;
    if (isGroupedRow(record)) return;

    onRowClick && onRowClick(id, index);
  };

  const getRowClassName = (record, index) => transactionsHelpers.getRowClassName({
    groupedPropName,
    index,
    record,
    selectedRowIndex
  });

  return (
    <StyledTableContainer {...rest}>
      <TotalSpend>
        {t('total')} {t('spend')}: <span className='price'>{gMV(totalSpend)}</span>
      </TotalSpend>
      <StyledTransactionTable
        columns={columns}
        dataSource={data}
        loading={loading}
        onRow={(record, index) => ({
          onClick: (e) => handleOnRowClick(e, record, index)
        })}
        pagination={false}
        rowClassName={getRowClassName}
        rowKey='created_date'
      />
    </StyledTableContainer>
  );
}

TransactionTable.propTypes = {
  data: PropTypes.array,
  edit: PropTypes.bool,
  loading: PropTypes.bool,
  onExpenseUpdate: PropTypes.func,
  onRowClick: PropTypes.func,
  selectedRowIndex: PropTypes.any,
  totalSpend: PropTypes.number
}

const mapStateToProps = state => {
  const {categories} = state.main;
  return {
    categories,
  }
}

export default connect(mapStateToProps, null)(TransactionTable);
